// ============================================================================
// Spray Module TypeScript Types
// ============================================================================

import type { Numeric, NumericValue } from '@/utils/numeric';

export type { Numeric, NumericValue };

export type SprayStatus = 'created' | 'assigned' | 'in_progress' | 'paused' | 'completed' | 'cancelled';
/** Orchard ground. */
export type OrchardTargetType = 'farm' | 'block' | 'cultivar_area';
/**
 * Nursery stock (docs/nursery-spraying-plan.md):
 *   nursery_block     a whole nursery block          target_id = block id
 *   nursery_row       one row, or one side of a row  target_id = block id, row_number, row_side
 *   nursery_location  one row position, or a place that is not a row
 *                                                    target_id = nursery location id
 * A spray is one or the other, never both.
 */
export type NurseryTargetType = 'nursery_block' | 'nursery_row' | 'nursery_location';
export type TargetType = OrchardTargetType | NurseryTargetType;

export const NURSERY_TARGET_TYPES: readonly NurseryTargetType[] = ['nursery_block', 'nursery_row', 'nursery_location'];
export const isNurseryTargetType = (t: string): t is NurseryTargetType =>
  (NURSERY_TARGET_TYPES as readonly string[]).includes(t);

/** How a nursery spray goes on. */
export type ApplicationMethod = 'foliar_runoff' | 'boom_over_pots' | 'drench' | 'granular';
export const APPLICATION_METHOD_LABELS: Record<ApplicationMethod, string> = {
  foliar_runoff: 'Foliar, to run-off',
  boom_over_pots: 'Boom over pots',
  drench: 'Drench',
  granular: 'Granular',
};
/** `weed_strip`: a strip sprayer wets only its strip of each row it drives, so
 *  the spray covers strip width / row spacing of every block. */
export type RowSpacingPattern = 'every_row' | 'every_second_row' | 'every_third_row' | 'custom' | 'weed_strip';

// ============================================================================
// CHEMICALS
// ============================================================================

export interface ActiveIngredient {
  name: string;
  concentration_gl?: number;
  concentration_pct?: number;
  cas_number?: string;
}

export interface Chemical {
  id: number;
  product_name: string;
  trade_name?: string | null;
  manufacturer?: string | null;
  active_ingredients: ActiveIngredient[];
  formulation_type?: string | null;
  signal_word?: string | null;
  ppe_required?: string[] | null;
  sds_url?: string | null;
  label_url?: string | null;
  notes?: string | null;
  barcode?: string | null;
  apvma_approval_number?: string | null;
  mode_of_action_group?: string | null;
  mode_of_action_type?: string | null;
  registration_status?: string | null;
  date_registered?: string | null;
  registration_expiry?: string | null;
  product_class?: string | null;
  category?: string | null;
  poison_schedule?: string | null;
  label_reg_number?: string | null;
  withholding_text?: string | null;
  // Tank-mix compatibility. The structured pairwise rules live separately in
  // ChemicalIncompatibility; these are the product's own properties.
  /** Slot in the tank-filling sequence, 1 = first in. Null = infer from formulation. */
  mix_order?: number | null;
  /** The label's COMPATIBILITY section, verbatim where possible. */
  compatibility_notes?: string | null;
  ph_min?: Numeric;
  ph_max?: Numeric;
  max_water_hardness_ppm?: number | null;
  /** Label instructs a jar test before any tank mix. */
  requires_jar_test?: boolean;
  compatibility_source_url?: string | null;
  compatibility_checked_at?: string | null;
  // Disease protection. Null means "derive it": activity and kickback from the
  // FRAC group, wash-off from the 25 mm rule (HHNodeServer fungicideActivity).
  disease_activity?: 'protectant' | 'curative' | 'both' | null;
  washoff_rain_mm?: Numeric;
  kickback_hours?: number | null;
  is_active: boolean;
  registration_count?: NumericValue;
  created_at: string;
  updated_at: string;
}

/**
 * Result of a `?barcode=` lookup against the chemical registry.
 * The barcode may match either `chemicals.barcode` or a `chemical_pack_sizes.barcode`;
 * when it matched a pack size, the `matched_pack_*` fields describe that pack.
 */
export interface BarcodeChemicalMatch {
  id: number;
  product_name: string;
  trade_name?: string | null;
  manufacturer?: string | null;
  formulation_type?: string | null;
  signal_word?: string | null;
  barcode?: string | null;
  matched_pack_size_id?: number | null;
  matched_pack_size?: number | string | null;
  matched_pack_unit?: string | null;
}

export interface ChemicalStateRegistration {
  state: string;
  status_code?: string | null;
  status_label?: string | null;
  first_registered?: string | null;
}

// Rate options (per crop registration). Each of these describes ONE way to
// dose the chemical for a given crop/pest — e.g. a per-hectare concentrate
// rate and a per-100L dilute rate. The operator picks one at spray-creation
// time. `min_rate`/`max_rate` are in the row's own `rate_unit`.
export type RateUnit = 'L/ha' | 'mL/ha' | 'kg/ha' | 'g/ha' | 'mL/100L' | 'g/100L';

export interface ChemicalRegistrationRate {
  id?: number;
  registration_id?: number;
  label?: string | null;
  min_rate?: Numeric;
  max_rate?: Numeric;
  rate_unit: RateUnit | string;
  is_default?: boolean;
  sort_order?: number;
  notes?: string | null;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ChemicalCropRegistration {
  id: number;
  chemical_id: number;
  situation_id?: number | null;
  situation_name?: string | null;
  registration_number?: string | null;
  target_pest?: string | null;
  min_rate_per_ha?: Numeric;
  max_rate_per_ha?: Numeric;
  rate_unit: string;
  // Full list of rate options. Mirrors the parent's min/max/rate_unit as the
  // default row. May be empty for APVMA-imported / bulk-rate registrations, in
  // which case the parent min/max/rate_unit are the single source of truth.
  rates?: ChemicalRegistrationRate[];
  max_applications_per_season?: number | null;
  min_days_between_apps?: number | null;
  whp_days?: number | null;
  whp_export_days?: number | null;
  rei_hours?: number | null;
  growth_stage_restriction?: string | null;
  notes?: string | null;
  source?: 'manual' | 'apvma' | 'label';
  /** 'proposed' = read off the APVMA label, awaiting a Spray Admin's check. */
  rate_status?: 'entered' | 'missing' | 'proposed';
  /** Where a proposed rate was read from. */
  label_source_url?: string | null;
  label_source_page?: number | null;
  label_extracted_at?: string | null;
  apvma_hostcode?: string | null;
  apvma_pestcode?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ApvmaRegPest {
  pestcode: string;
  pest: string;
  already: boolean;
}

export interface ApvmaRegSituation {
  hostcode: string;
  crop: string;
  pests: ApvmaRegPest[];
  pest_count: number;
  already: number;
  all_imported: boolean;
}

export interface ApvmaRegistrationsPreview {
  pcode: string;
  total_combos: number;
  situations: ApvmaRegSituation[];
  unknown_host_combos: number;
}

// ============================================================================
// APVMA LABEL EXTRACTION
// ============================================================================

/**
 * One Directions-for-Use line read off the APVMA approved label.
 *
 * `match` says what the row can do. Rates are not in the APVMA open dataset,
 * so filling a registration that PUBCRIS already vouched for is both safer and
 * more useful than inventing one; a row whose crop/pest the reader could not
 * pair with an existing registration is reported rather than guessed at.
 */
export interface LabelProposalRow {
  key: string;
  /** Page of the label PDF the row was read from, so a reviewer can check it. */
  page: number;
  crop_text: string;
  pest_text: string;
  state_text?: string | null;
  /** The rate cell verbatim, so a reviewer can see what was interpreted. */
  rate_text: string;
  whp_text?: string | null;
  match: {
    kind: 'update' | 'unmatched';
    registration_id?: number;
    situation_id?: number;
    situation_name?: string;
    target_pest?: string;
    /** The registration already carries a rate; applying would overwrite it. */
    had_rate?: boolean;
    score?: number;
  };
  min_rate_per_ha: number | null;
  max_rate_per_ha: number | null;
  rate_unit: string | null;
  whp_days: number | null;
  whp_export_days: number | null;
  rei_hours: number | null;
  notes: string | null;
  /** Matched to a registration AND carries a parseable rate. */
  applicable: boolean;
}

export interface LabelProposal {
  label: {
    url: string;
    bytes: number;
    pages: number;
    product_name: string | null;
    apvma_approval_number: string;
    has_tables: boolean;
  };
  /** Metadata fields the record is missing; fields it already has are skipped. */
  chemical: Partial<Pick<Chemical,
    'signal_word' | 'mode_of_action_group' | 'mode_of_action_type'
    | 'ppe_required' | 'withholding_text' | 'compatibility_notes'>>;
  chemical_skipped: { field: string; current: unknown; proposed: unknown }[];
  rows: LabelProposalRow[];
  summary: {
    rows_parsed: number;
    ready: number;
    already_rated: number;
    unmatched: number;
    no_rate_parsed: number;
    registrations_on_record: number;
    missing_rates_on_record: number;
  };
}

export interface LabelApplyResult {
  chemicalUpdated: boolean;
  registrationsUpdated: number;
  errors: { kind: string; message: string }[];
}

export interface ChemicalPackSize {
  id: number;
  chemical_id: number;
  size: NumericValue;
  unit: string;
  barcode?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ChemicalDetail extends Chemical {
  registrations: ChemicalCropRegistration[];
  pack_sizes: ChemicalPackSize[];
}

// Chemical with registration details (for the "by crop type" endpoint)
export interface ChemicalWithRegistration extends Chemical {
  registration_id: number;
  target_pest?: string | null;
  min_rate_per_ha?: Numeric;
  max_rate_per_ha?: Numeric;
  rate_unit: string;
  whp_days?: number | null;
  whp_export_days?: number | null;
  rei_hours?: number | null;
  max_applications_per_season?: number | null;
  min_days_between_apps?: number | null;
  growth_stage_restriction?: string | null;
  // Rate options for this registration — offered as a choice in the spray
  // wizard. Empty when the registration only has the mirrored default rate.
  rates?: ChemicalRegistrationRate[];
  // Set on the by-situation list: which covering situation this registration
  // was filed under, and how it relates to the one being sprayed.
  matched_situation_id?: number | null;
  matched_situation_name?: string | null;
  matched_relation?: SituationRelation | null;
  situation_caveat?: string | null;
}

// ============================================================================
// INVENTORY
// ============================================================================

export interface ChemicalInventoryLocation {
  id: number;
  organisation_id: number;
  name: string;
  description?: string | null;
  is_active: boolean;
  farm_id?: number | null;
  farm_name?: string | null;
  chemical_count?: NumericValue;
  created_at: string;
  updated_at: string;
}

export interface InventoryStockLevel {
  chemical_id: number;
  product_name: string;
  trade_name?: string | null;
  formulation_type?: string | null;
  location_id: number;
  location_name: string;
  unit: string;
  current_stock: NumericValue;
  last_movement_at: string;
}

export interface InventoryTransaction {
  id: number;
  organisation_id: number;
  location_id: number;
  location_name?: string;
  chemical_id: number;
  product_name?: string;
  trade_name?: string | null;
  transaction_type: 'purchase' | 'spray_usage' | 'adjustment' | 'disposal';
  quantity: NumericValue;
  unit: string;
  reference_id?: number | null;
  reference_type?: string | null;
  supplier?: string | null;
  purchase_date?: string | null;
  batch_number?: string | null;
  expiry_date?: string | null;
  unit_cost?: Numeric;
  notes?: string | null;
  recorded_by?: number | null;
  recorded_by_first_name?: string | null;
  recorded_by_last_name?: string | null;
  recorded_at: string;
  created_at: string;
}

// ============================================================================
// SPRAY APPLICATIONS
// ============================================================================

export interface SprayTarget {
  id: number;
  target_type: TargetType;
  target_id: number;
  target_name: string;
  area_hectares?: Numeric;
  /** nursery_row only. A null side is the whole row. */
  row_number?: number | null;
  row_side?: 'left' | 'right' | null;
}

/**
 * A lot a nursery spray landed on, copied when the spray was planned and
 * again when it was completed (`basis`), so the record still reads after the
 * lot is split, moved or sold.
 */
export interface SprayTargetLot {
  id: number;
  spray_target_id: number | null;
  lot_id: number | null;
  location_id: number | null;
  lot_code: string | null;
  location_name: string | null;
  cultivar_name: string | null;
  rootstock_name: string | null;
  tree_type_name: string | null;
  plant_count: number;
  basis: 'planned' | 'sprayed';
  captured_at: string;
}

/** One spray on a lot's spray history. */
export interface LotSprayHistoryEntry {
  id: number;
  reference_number: string | null;
  title: string;
  status: SprayStatus;
  scheduled_date: string;
  started_at: string | null;
  completed_at: string | null;
  application_method: ApplicationMethod | null;
  basis: 'planned' | 'sprayed';
  plant_count: number;
  location_name: string | null;
  chemicals: {
    product_name: string;
    rate: Numeric;
    rate_unit: string | null;
    concentration_factor: Numeric;
    target_pest: string | null;
    rei_hours: number | null;
  }[];
}

export interface WeatherSnapshot {
  temp_c?: Numeric;
  wind_kph?: Numeric;
  wind_dir?: string | null;
  humidity?: Numeric;
  precip_mm?: Numeric;
  conditions?: string | null;
  fetched_at?: string | null;
}

/**
 * Whether one machine on the rig has had its pre-start walk-around today.
 *
 * `satisfied` is the only field the gate reads, and it is NOT the same as
 * `checked_today`: a machine with no checklist configured for its type has
 * nothing to complete and passes. The other fields exist so the UI can say
 * which of those two it is, instead of showing one tick that means either.
 *
 * `last_completion.overall_pass` is reported but does not affect `satisfied`
 * — the gate asks that somebody looked, not that they liked what they saw.
 */
export interface PreStartMachineStatus {
  equipment_id: number;
  equipment_name: string;
  equipment_type: string;
  custom_type_label?: string | null;
  role: 'sprayer' | 'prime_mover';
  /** A checklist template exists for this equipment type. */
  has_template: boolean;
  /** Checked today, in the organisation's timezone. */
  checked_today: boolean;
  satisfied: boolean;
  last_completion?: {
    id: number;
    completed_at: string;
    overall_pass: boolean;
    template_name?: string | null;
    completed_by_first_name?: string | null;
    completed_by_last_name?: string | null;
    age_days?: number | null;
  } | null;
}

export interface SprayApplication {
  id: number;
  organisation_id: number;
  reference_number?: string | null;
  title: string;

  // Chemical
  chemical_id: number;
  chemical_name?: string;
  chemical_trade_name?: string | null;
  signal_word?: string | null;
  ppe_required?: string[] | null;
  crop_registration_id?: number | null;
  target_pest?: string | null;
  whp_days?: number | null;
  whp_export_days?: number | null;
  rei_hours?: number | null;
  registration_number?: string | null;
  growth_stage_restriction?: string | null;

  // Rate & concentration
  rate_per_ha?: Numeric;
  rate_unit: string;
  concentration_factor: NumericValue;

  // Partial-area
  row_spacing_pattern: RowSpacingPattern;
  row_pattern_custom?: string | null;
  spray_coverage_pct: NumericValue;

  // Calculated areas & volumes
  total_target_area_ha?: Numeric;
  effective_area_ha?: Numeric;
  total_volume_mixed?: Numeric;
  total_chemical_used?: Numeric;
  inventory_location_id?: number | null;
  inventory_location_name?: string | null;

  // Schedule
  scheduled_date: string;
  scheduled_start_time?: string | null;
  scheduled_end_time?: string | null;

  // Status
  status: SprayStatus;

  // Personnel
  created_by: number;
  creator_name?: string;
  assigned_operator_id?: number | null;
  operator_name?: string | null;
  operator_email?: string | null;

  // Execution
  started_at?: string | null;
  completed_at?: string | null;
  signed_off_by?: number | null;
  signed_off_by_name?: string | null;
  sign_off_location_lat?: Numeric;
  sign_off_location_lng?: Numeric;

  // Equipment
  equipment_used?: string | null;
  nozzle_type?: string | null;
  equipment_id?: number | null;                  // sprayer (implement)
  equipment_name?: string | null;
  equipment_type?: string | null;
  /** The sprayer's geometry, for the calibration wizard. */
  equipment_boom_width_m?: Numeric;
  equipment_nozzle_spacing_cm?: Numeric;
  equipment_strip_width_m?: Numeric;
  equipment_operating_speed_kph?: Numeric;
  prime_mover_equipment_id?: number | null;      // tractor pulling the sprayer
  prime_mover_equipment_name?: string | null;
  prime_mover_equipment_type?: string | null;

  // Weather. Three snapshots, each frozen onto the record when it was taken:
  // the forecast at creation, conditions at start, conditions at sign-off.
  // wx_actual_* IS the completion snapshot — it was named before a start one
  // existed and kept its name rather than break shipped mobile bundles.
  // `_source` is 'station' | 'open_meteo', or null on rows written before
  // provenance was recorded (it renders no badge rather than guessing).
  wx_forecast_temp_c?: Numeric;
  wx_forecast_wind_kph?: Numeric;
  wx_forecast_wind_dir?: string | null;
  wx_forecast_humidity?: Numeric;
  wx_forecast_precip_mm?: Numeric;
  wx_forecast_conditions?: string | null;
  wx_forecast_fetched_at?: string | null;
  wx_start_temp_c?: Numeric;
  wx_start_wind_kph?: Numeric;
  wx_start_wind_dir?: string | null;
  wx_start_humidity?: Numeric;
  wx_start_precip_mm?: Numeric;
  wx_start_conditions?: string | null;
  wx_start_source?: string | null;
  wx_start_fetched_at?: string | null;
  wx_actual_temp_c?: Numeric;
  wx_actual_wind_kph?: Numeric;
  wx_actual_wind_dir?: string | null;
  wx_actual_humidity?: Numeric;
  wx_actual_precip_mm?: Numeric;
  wx_actual_conditions?: string | null;
  wx_actual_source?: string | null;
  wx_actual_fetched_at?: string | null;

  // Notes
  pre_spray_notes?: string | null;
  post_spray_notes?: string | null;
  cancellation_reason?: string | null;

  // Tank-mix reconciliation, computed at completion and then frozen.
  tanks_used?: number | null;
  total_water_l?: Numeric;
  actual_area_ha?: Numeric;
  actual_area_source?: string | null;
  label_compliance?: LabelCompliance | null;
  reconciliation?: SprayReconciliation | null;
  reconciled_at?: string | null;

  // Calibration gate. The creator demands a fresh calibration; calibration_id
  // is what satisfies it, so a calibration done months ago cannot answer a
  // demand made today. Enforced as a hard block on start.
  calibration_required?: boolean;
  calibration_id?: number | null;
  /** The machine's current calibration, with its age and staleness. */
  latest_calibration?: EquipmentCalibration | null;
  /**
   * The calibration this spray was actually RUN at, frozen at completion.
   * A finished spray is evidence, so it reads from here rather than from the
   * machine — recalibrating the sprayer afterwards cannot rewrite it. Null on
   * a spray that has not completed, and on one whose sprayer had never been
   * calibrated when it ran.
   */
  applied_calibration_id?: number | null;
  applied_calibration?: EquipmentCalibration | null;

  /**
   * Today's pre-start state for every machine on the rig — the sprayer and
   * the tractor pulling it, deduplicated for a self-propelled unit. Always
   * present (empty when the spray names no machine), so nothing has to branch
   * on whether this spray "has" one.
   */
  pre_start_status?: PreStartMachineStatus[];

  // Nursery spraying
  /** Aimed at nursery stock rather than orchard ground. */
  is_nursery?: boolean;
  target_lots?: SprayTargetLot[];
  target_plant_count?: number | null;
  application_method?: ApplicationMethod | null;
  runoff_l_per_plant?: Numeric;

  // Relations
  targets: SprayTarget[];
  /** Every product in the tank mix. A single-product spray is a mix of one. */
  chemicals?: SprayMixChemical[];
  /**
   * Longest withholding / re-entry across the WHOLE mix. A tank mix is only as
   * free to harvest as its most restrictive product, so reading whp_days off
   * the primary alone understates it whenever a partner is stricter.
   */
  mix_whp_days?: number | null;
  mix_rei_hours?: number | null;

  // Withholding period vs the target blocks' harvest windows.
  whp_days_applied?: number | null;
  /** scheduled_date + the mix's longest WHP: the first day fruit may be picked. */
  whp_clear_date?: string | null;
  whp_conflicts?: WhpConflict[] | null;
  whp_override_status?: WhpOverrideStatus;
  whp_override_reason?: string | null;
  whp_override_requested_by?: number | null;
  whp_override_requested_by_name?: string | null;
  whp_override_requested_at?: string | null;
  whp_override_decided_by?: number | null;
  whp_override_decided_by_name?: string | null;
  whp_override_decided_at?: string | null;
  whp_override_decision_notes?: string | null;
  waypoint_count?: number;

  created_at: string;
  updated_at: string;
}

// ============================================================================
// WITHHOLDING PERIOD vs HARVEST
// ============================================================================

/**
 * none     — no conflict; nothing to decide
 * pending  — held: the spray can be neither assigned nor started until a Spray
 *            Admin decides. The creator raises it, management clears it.
 * approved — a Spray Admin accepted the conflict, against the snapshot below
 * rejected — a Spray Admin refused it; the spray stays blocked
 */
export type WhpOverrideStatus = 'none' | 'pending' | 'approved' | 'rejected';

export interface WhpConflict {
  cultivar_area_id: number;
  area_name: string;
  block_name?: string | null;
  farm_name?: string | null;
  cultivar_name?: string | null;
  season_year: number;
  earliest_harvest_date: string;
  expected_end_date: string | null;
  harvest_status: 'estimate' | 'confirmed' | 'actual';
  /** Days the withholding period runs past the earliest harvest date. */
  days_short: number;
  /** in_harvest = picking is already open on the day of the spray. */
  severity: 'in_harvest' | 'whp_overrun';
}

/** A block with no harvest window on record — neither cleared nor faulted. */
export interface WhpUnverifiedArea {
  cultivar_area_id: number;
  area_name: string;
  block_name?: string | null;
  farm_name?: string | null;
  cultivar_name?: string | null;
}

export interface WhpAssessment {
  /** The longest WHP in the tank mix — a mix clears when its slowest product does. */
  whp_days: number | null;
  binding_product: { chemical_id: number; product_name: string } | null;
  scheduled_date: string;
  whp_clear_date: string | null;
  conflicts: WhpConflict[];
  unverified: WhpUnverifiedArea[];
  checked_area_count: number;
  /** Nursery stock: there is no harvest date. Never read as cleared. */
  not_applicable?: boolean;
}

/** A spray situation the targets' cultivars resolved to. */
/**
 * How a covering situation relates to the one being sprayed. `broader` is
 * one-way: its registrations cover this planting, never the reverse.
 */
export type SituationRelation = 'self' | 'synonym' | 'broader';

export interface SituationCoverageEntry {
  id: number;
  name: string;
  relation: SituationRelation;
  /** 0 for the situation's own group, 1 for the group above it, and so on. */
  depth: number;
  /** This row names its group. */
  canonical: boolean;
  caveat: string | null;
}

/** A spray situation as the admin grouping view sees it (`?all=true`). */
export interface SpraySituationDetail {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
  source?: 'manual' | 'apvma';
  apvma_hostcode?: string | null;
  /** Synonym of — another spelling of that group. */
  same_as_id: number | null;
  /** Broader situation whose registrations also cover this one. */
  parent_id: number | null;
  caveat: string | null;
  registration_count?: number;
  crop_types?: string[];
}

export interface ResolvedSituation {
  id: number;
  name: string;
  area_hectares: number;
  /** "Area name (Cultivar)" for each cultivar area that resolved here. */
  areas: string[];
  /** Every situation searched for this one. Absent from older servers. */
  coverage?: SituationCoverageEntry[];
  /** Nursery targets: the plants in the lots that resolved here. */
  plant_count?: number;
  /** Nursery targets: reached as the crop's nursery situation, not its own. */
  nursery_situation?: boolean;
  /** Nursery targets: the crop types of the lots that resolved here. */
  crop_types?: { id: number; name: string }[];
}

/** A target part that could not be resolved, and why — never guessed. */
export interface UnresolvedSituationTarget {
  target_type: 'block' | 'cultivar_area' | NurseryTargetType | 'nursery_lot';
  target_id: number;
  name: string | null;
  reason: 'no_cultivar_areas' | 'no_cultivar' | 'no_crop_type' | 'crop_type_unlinked' | 'no_lots';
  row_number?: number | null;
  row_side?: 'left' | 'right' | null;
  cultivar_name?: string | null;
  crop_type_id?: number | null;
  crop_type_name?: string | null;
}

export interface SituationResolution {
  /** Largest area first. */
  situations: ResolvedSituation[];
  unresolved: UnresolvedSituationTarget[];
}

// ============================================================================
// FORM DATA TYPES (for create/update forms)
// ============================================================================

export interface SprayTargetInput {
  target_type: TargetType;
  target_id: number;
  /** nursery_row only. A null side is the whole row. */
  row_number?: number | null;
  row_side?: 'left' | 'right' | null;
}

export interface SprayApplicationFormData {
  title: string;
  chemical_id: number;
  crop_registration_id?: number | null;
  rate_per_ha?: number | null;
  rate_unit: string;
  concentration_factor: number;
  row_spacing_pattern: RowSpacingPattern;
  row_pattern_custom?: string;
  spray_coverage_pct?: number;
  inventory_location_id?: number | null;
  scheduled_date: string;
  scheduled_start_time?: string;
  scheduled_end_time?: string;
  equipment_used?: string;
  nozzle_type?: string;
  equipment_id?: number | null;              // sprayer (implement)
  prime_mover_equipment_id?: number | null;  // tractor pulling the sprayer
  pre_spray_notes?: string;
  /** Demand a fresh calibration before the operator may start. */
  calibration_required?: boolean;
  /** Nursery: how it goes on, and the run-off the operator states. */
  application_method?: ApplicationMethod | null;
  runoff_l_per_plant?: number | null;
  targets: SprayTargetInput[];
  /**
   * The tank mix. When present it is the source of truth and the flat
   * chemical_id / rate_per_ha fields above are derived from its primary
   * product server-side. The flat fields remain accepted on their own so an
   * older mobile client still creates a valid mix of one.
   */
  chemicals?: SprayMixChemicalInput[];
  /**
   * Why the spray is being planned even though its withholding period runs past
   * a target block's harvest date. Required when conflicts exist — the server
   * refuses the create otherwise — and the spray is then held for a Spray Admin.
   */
  whp_override_reason?: string;
}

export interface SignOffData {
  sign_off_location_lat?: number | null;
  sign_off_location_lng?: number | null;
  post_spray_notes?: string;
  total_chemical_used?: number | null;
  total_volume_mixed?: number | null;
  /**
   * Overrides the area the applied-rate check divides by, for when the GPS
   * track is known to be wrong — tracking started late, phone left in the cab.
   */
  actual_area_ha?: number | null;
}

// ============================================================================
// GPS / LOCATION
// ============================================================================

export interface WaypointInput {
  lat: number;
  lng: number;
  accuracy?: number | null;
  altitude?: number | null;
  heading?: number | null;
  speed?: number | null;
  recorded_at?: string;
}

export interface LocationRecord {
  id: number;
  latitude: NumericValue;
  longitude: NumericValue;
  accuracy?: number | null;
  altitude?: number | null;
  heading?: number | null;
  speed?: number | null;
  recorded_at: string;
}

export interface CoverageResult {
  covered_pct: number | null;
  uncovered_zones: { lat: number; lng: number }[];
  total_waypoints: number;
  target_area_ha: number;
  total_grid_cells?: number;
  message?: string;
}

// ============================================================================
// ADVISORS
// ============================================================================

export type AdvisorPermission = 'create_spray' | 'view_spray' | 'edit_spray' | 'manage_chemicals';

export interface SprayOrgAdvisor {
  id: number;
  advisor_user_id: number;
  advisor_first_name: string;
  advisor_last_name: string;
  advisor_email: string;
  advisor_org_id: number;
  advisor_org_name: string;
  target_org_id: number;
  permissions: AdvisorPermission[];
  granted_by?: number | null;
  granted_by_first_name?: string | null;
  granted_by_last_name?: string | null;
  granted_at: string;
  expires_at?: string | null;
  is_active: boolean;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface MyAdvisoryOrg {
  id: number;
  target_org_id: number;
  target_org_name: string;
  permissions: AdvisorPermission[];
  expires_at?: string | null;
  is_active: boolean;
}

// ============================================================================
// TANK-MIX COMPATIBILITY
// ============================================================================

/**
 * Severity of a compatibility rule. `compatible` is a real, useful value —
 * "the label says these two are fine together" is worth recording, because it
 * stops an operator re-running the same jar test every season.
 */
export type CompatibilitySeverity = 'incompatible' | 'caution' | 'compatible';

/** How a rule named its partner. Product is most specific, formulation least. */
export type CompatibilityMatchKind = 'product' | 'active_ingredient' | 'moa_group' | 'formulation';

export interface ChemicalIncompatibility {
  id: number;
  chemical_id: number;
  /** null = global rule from a label, shared across orgs. Set = this org's own. */
  organisation_id?: number | null;
  other_chemical_id?: number | null;
  other_product_name?: string | null;
  other_trade_name?: string | null;
  other_active_ingredient?: string | null;
  other_moa_group?: string | null;
  other_formulation_type?: string | null;
  severity: CompatibilitySeverity;
  reason?: string | null;
  source_url?: string | null;
  notes?: string | null;
  created_by_first_name?: string | null;
  created_by_last_name?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface IncompatibilityInput {
  other_chemical_id?: number | null;
  other_active_ingredient?: string;
  other_moa_group?: string;
  other_formulation_type?: string;
  severity: CompatibilitySeverity;
  reason?: string;
  source_url?: string;
  notes?: string;
  /** 'global' = from a label, shared. 'organisation' = this farm's experience. */
  scope?: 'global' | 'organisation';
}

export interface MixFinding {
  severity: CompatibilitySeverity;
  kind: 'rule' | 'ph_conflict';
  match_kind?: CompatibilityMatchKind;
  a_chemical_id: number;
  a_product_name: string;
  b_chemical_id: number;
  b_product_name: string;
  message: string;
  matched_on?: string | null;
  reason?: string | null;
  source_url?: string | null;
  scope: 'global' | 'organisation' | 'derived';
  rule_id?: number;
}

export interface MixFillOrderEntry {
  chemical_id: number;
  product_name: string;
  formulation_type?: string | null;
  /** 1 = first into the water. */
  sequence: number;
  group_label: string;
  /** true when derived from formulation type rather than set on the product. */
  inferred: boolean;
  position: number;
}

/**
 * Result of checking a set of products against each other.
 *
 * `verdict: 'ok'` means nothing is recorded against this mix — NOT that it has
 * been certified safe. For most pairs no rule exists, which is simply the
 * truth, and the UI must say so in those words.
 */
export interface MixCheckResult {
  chemicals: Array<Pick<Chemical, 'id' | 'product_name' | 'trade_name' | 'formulation_type'> & {
    mix_order?: number | null;
    requires_jar_test?: boolean;
    ph_min?: Numeric;
    ph_max?: Numeric;
    compatibility_notes?: string | null;
  }>;
  fill_order: MixFillOrderEntry[];
  findings: MixFinding[];
  verdict: 'ok' | 'caution' | 'incompatible';
  jar_test_required: boolean;
  jar_test_products: { chemical_id: number; product_name: string }[];
  checked_pairs: number;
}

// ============================================================================
// TANK MIX — the products on a spray
// ============================================================================

/**
 * One product in a spray's tank mix. A single-product spray is a mix of one.
 *
 * The registration fields are a SNAPSHOT taken when the spray was created, not
 * a live join — reconciliation judges the applied rate against the label as it
 * stood on the day, and the record must still read correctly after a
 * registration is revised or deactivated.
 */
export interface SprayMixChemical {
  id: number;
  spray_application_id: number;
  chemical_id: number;
  crop_registration_id?: number | null;

  /** 'fertiliser' for a foliar fertiliser in the tank: no registration, no label rate. */
  product_kind?: 'chemical' | 'fertiliser' | null;
  product_name: string;
  trade_name?: string | null;
  manufacturer?: string | null;
  formulation_type?: string | null;
  signal_word?: string | null;
  ppe_required?: string[] | null;
  mix_order?: number | null;
  requires_jar_test?: boolean;
  active_ingredients?: ActiveIngredient[];
  mode_of_action_group?: string | null;

  rate?: Numeric;
  rate_unit: string;
  concentration_factor: NumericValue;

  target_pest?: string | null;
  registration_number?: string | null;
  min_rate?: Numeric;
  max_rate?: Numeric;
  whp_days?: number | null;
  whp_export_days?: number | null;
  rei_hours?: number | null;
  growth_stage_restriction?: string | null;

  mix_sequence: number;
  /** Mirrored onto the application's legacy chemical_id / rate_per_ha columns. */
  is_primary: boolean;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

/** What the create/edit forms post for each product in the mix. */
export interface SprayMixChemicalInput {
  chemical_id: number;
  crop_registration_id?: number | null;
  rate?: number | null;
  rate_unit: string;
  concentration_factor?: number;
  mix_sequence?: number;
  is_primary?: boolean;
  notes?: string;
}

// ============================================================================
// TANK LOADS
// ============================================================================

export type ProductUnit = 'L' | 'mL' | 'kg' | 'g';

/**
 * How a load's sprayed area was arrived at. Worth surfacing: a GPS-derived
 * area and a typed one carry very different weight in an audit.
 */
export type TankAreaSource = 'pending' | 'gps' | 'manual' | 'apportioned';

export interface TankLoadChemical {
  id: number;
  tank_load_id: number;
  chemical_id: number;
  spray_application_chemical_id?: number | null;
  product_name?: string;
  trade_name?: string | null;
  formulation_type?: string | null;
  /** What the operator actually poured. */
  declared_amount?: Numeric;
  declared_unit: ProductUnit;
  /** What the plan called for at this tank's water volume. */
  expected_amount?: Numeric;
  expected_unit?: ProductUnit | null;
  batch_number?: string | null;
  expiry_date?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * The verdict on one load's water rate.
 *
 * `unverifiable` is a first-class and common outcome, not a failure: an
 * untracked spray, an uncalibrated sprayer, or a load that spent its pass
 * re-covering ground an earlier tank had all land here. It carries a reason,
 * and the UI shows it rather than a shrug.
 */
export type TankRateStatus = 'ok' | 'high' | 'low' | 'unverifiable';

/**
 * One tank load an operator mixed and sprayed out. Opened with "Start tank N",
 * closed when the next tank starts or the spray completes — the time window is
 * what makes per-tank GPS area possible.
 */
export interface SprayTankLoad {
  id: number;
  spray_application_id: number;
  tank_number: number;
  planned_water_l?: Numeric;
  water_volume_l?: Numeric;
  started_at: string;
  finished_at?: string | null;
  start_lat?: Numeric;
  start_lng?: Numeric;
  end_lat?: Numeric;
  end_lng?: Numeric;
  area_sprayed_ha?: Numeric;
  area_source: TankAreaSource;

  // The per-tank water rate check, run as the load closes. Advisory: it never
  // blocks an operator, and null status means nobody has looked yet — which is
  // not the same as having looked and been unable to tell.
  /** Water this load actually put out per hectare: water ÷ area covered. */
  applied_rate_lpha?: Numeric;
  /** What the calibration says it should have, over the ground THIS load covered. */
  expected_rate_lpha?: Numeric;
  /** Signed. Positive means more water went out than the calibration predicts. */
  rate_variance_pct?: Numeric;
  rate_check_status?: TankRateStatus | null;
  /** Why a load could not be judged — only set alongside 'unverifiable'. */
  rate_check_reason?: string | null;
  /** Litres sprayed while turning at the row ends, left out of applied_rate_lpha.
   *  Null when none was: sprayer off for turns, a typed area, or an older check. */
  turn_water_l?: Numeric | null;
  rate_checked_at?: string | null;

  /** Final load deliberately part-filled — stops it reading as an under-dose. */
  is_partial: boolean;
  /**
   * The operator shut the sprayer off for the headland turns, so turning time
   * is left out of the load's implied flow. Carried forward to the next load.
   */
  sprayer_off_on_turns?: boolean;
  notes?: string | null;
  recorded_by?: number | null;
  recorded_by_first_name?: string | null;
  recorded_by_last_name?: string | null;
  chemicals: TankLoadChemical[];
  created_at: string;
  updated_at: string;
}

/**
 * How one load was driven, from the GPS track. Every field is nullable: a
 * figure the track could not support is null with `reason` saying why, never
 * a zero. Mirrors `sprayTankMotion.computeTankMotion` on the server.
 */
export interface TankMotion {
  tank_load_id: number;
  tank_number: number;
  is_open: boolean;
  point_count: number;
  in_field_point_count: number;
  /** device = the phone's own Doppler speed; track = distance between fixes. */
  speed_source: 'device' | 'track' | null;
  speed_sample_count: number;
  median_speed_kph: number | null;
  p10_speed_kph: number | null;
  p90_speed_kph: number | null;
  track_speed_kph: number | null;
  spraying_sec: number;
  /** Short trips out of the block and back: headland turns. Absent from older servers. */
  turning_sec?: number;
  /** Turning time was left out of the flow because the sprayer was off for it. */
  sprayer_off_on_turns?: boolean;
  stopped_sec: number;
  transit_sec: number;
  gap_sec: number;
  /**
   * Water ÷ minutes moving in the block, plus turning at the row ends unless
   * the sprayer was off for them. Closed loads only.
   */
  implied_flow_lpm: number | null;
  expected_speed_kph: number | null;
  expected_flow_lpm: number | null;
  speed_variance_pct: number | null;
  flow_variance_pct: number | null;
  /** The L/ha variance speed, flow (and width, when measured) predict together. */
  predicted_rate_variance_pct: number | null;
  predicted_includes_width: boolean;
  pass_count: number;
  /** Median gap to the nearest pass either side — by position, not order. */
  pass_spacing_m: number | null;
  row_spacing_m: number | null;
  /** A boom's width, or one row for an airblast: what the calibration assumed. */
  expected_pass_spacing_m: number | null;
  width_variance_pct: number | null;
  /** A weed-strip sprayer's wetted width: passes are a row apart, but each
   *  treats only this much. Null for any other sprayer. */
  sprayed_width_m?: number | null;
  driven_area_ha: number | null;
  reason: string | null;
}

export interface TankMotionBlock {
  target_key: string;
  target_name?: string | null;
  row_spacing_m: number | null;
  pass_count: number;
  row_positions: number;
  typical_gap_m: number | null;
  axis_deg: number | null;
}

export interface SprayTankMotion {
  calibration: {
    speed_kph: number | null;
    total_flow_lpm: number | null;
    reference_swath_m: number | null;
    swath_source: SwathSource | null;
  } | null;
  tanks: TankMotion[];
  blocks: TankMotionBlock[];
}

/**
 * Water still needed to finish a spray, from
 * `GET /spray/applications/:id/tanks/make-up` (HHNodeServer utils/tankMakeUp).
 * The open tank is counted as going out in full.
 */
export type TankMakeUp =
  | { available: false; reason: 'no_planned_area' | 'no_rate' }
  | {
      available: true;
      planned_area_ha: number;
      covered_area_ha: number;
      remaining_area_ha: number;
      rate_lpha: number;
      /** `this_job`: water ÷ area of the tanks measured so far. */
      rate_basis: 'this_job' | 'calibration';
      calibrated_rate_lpha: number | null;
      measured_tanks: number;
      remaining_water_l: number;
      /** Rounded up to 10 L. */
      make_up_l: number;
      capacity_l: number | null;
      tanks_left: number | null;
      /** The next tank finishes the job. */
      is_last: boolean;
      finished: boolean;
    };

export interface StartTankInput {
  water_volume_l?: number | null;
  is_partial?: boolean;
  /** Omit to carry the previous load's answer forward. */
  sprayer_off_on_turns?: boolean;
  start_lat?: number | null;
  start_lng?: number | null;
  notes?: string;
  chemicals?: Array<{
    chemical_id: number;
    declared_amount?: number | null;
    declared_unit?: ProductUnit;
    batch_number?: string;
    expiry_date?: string;
    notes?: string;
  }>;
}

export interface UpdateTankInput {
  water_volume_l?: number | null;
  is_partial?: boolean;
  sprayer_off_on_turns?: boolean;
  area_sprayed_ha?: number | null;
  end_lat?: number | null;
  end_lng?: number | null;
  notes?: string;
  chemicals?: Array<{
    chemical_id: number;
    declared_amount?: number | null;
    declared_unit?: ProductUnit;
    batch_number?: string;
    expiry_date?: string;
    notes?: string;
  }>;
}

// ============================================================================
// RECONCILIATION
// ============================================================================

/** Verdict against the registered label range, per product and overall. */
export type LabelCompliance =
  | 'within_label'
  | 'above_label'
  | 'below_label'
  | 'mixed'
  | 'unverifiable';

export interface ReconciliationProduct {
  chemical_id: number;
  spray_application_chemical_id: number;
  product_name: string;
  rate_unit: string;
  planned_rate?: number | null;
  concentration_factor: number;
  label_min_rate?: number | null;
  label_max_rate?: number | null;
  unit: ProductUnit;
  /** Dosed against tank water (per 100 L) rather than against ground. */
  dilute: boolean;
  /** Sum of what the operator declared pouring, across every tank. */
  declared_total?: number | null;
  /** What the rate says should have gone out, for the same water/area. */
  computed_total?: number | null;
  total_used?: number | null;
  total_source: 'declared' | 'computed' | 'unknown';
  /** total_used ÷ actual area (or ÷ hundred-litres for a dilute rate). */
  applied_rate?: number | null;
  verdict: LabelCompliance;
  /** Declared vs computed, as a percentage of computed. */
  variance_pct?: number | null;
  /** True past ±10% — a mis-measure or a mis-recorded tank, both worth knowing. */
  variance_flag: boolean;
  unconvertible_entries: number;
}

export interface SprayReconciliation {
  tanks_used: number;
  total_water_l: number;
  planned_area_ha?: number | null;
  actual_area_ha?: number | null;
  /** gps | planned | manual — how the denominator was arrived at. */
  actual_area_source: string;
  output_rate_lpha?: number | null;
  /** equipment | calibration | calibration_per_target — whether the rate came
   *  from a measurement, and whether it was re-derived per block. */
  output_rate_source?: string;
  output_rate_per_target?: OutputRatePerTarget[];
  coverage?: {
    covered_ha: number;
    target_area_ha: number;
    covered_pct?: number | null;
    grid_cells: number;
    unattributed_cells: number;
  } | null;
  products: ReconciliationProduct[];
  /**
   * Every closed load's rate check, recomputed against the final attribution.
   * One averaged rate for the job cannot say WHEN it drifted; these can.
   */
  tank_checks?: TankRateCheck[];
  label_compliance: LabelCompliance;
  computed_at: string;
}

/** One load's rate check, as the reconciliation reports it. */
export interface TankRateCheck {
  tank_load_id: number;
  tank_number: number;
  water_l?: number | null;
  area_ha?: number | null;
  /** gps | manual — an apportioned area is never judged, so never appears here. */
  area_basis?: 'gps' | 'manual' | null;
  /** Litres sprayed while turning at the row ends, left out of the rate. */
  turn_water_l?: number | null;
  applied_rate_lpha?: number | null;
  expected_rate_lpha?: number | null;
  rate_variance_pct?: number | null;
  status: TankRateStatus;
  reason?: string | null;
}

// ============================================================================
// SPRAYER CALIBRATION
// ============================================================================

/**
 * How the flow was measured. Both methods exist to produce one number —
 * total flow in L/min — and differ only in what is practical to measure on the
 * machine in front of you.
 */
export type CalibrationMethod =
  /** Catch one jet for a minute at operating pressure, count the jets. Booms. */
  | 'nozzle'
  /** Run a known volume out at operating RPM and time it. Airblast. */
  | 'tank_empty';

/** Where the swath the rate was derived against came from. */
/** `strip`: a weed-strip sprayer's wetted width. Its L/ha is per sprayed
 *  hectare and, like a boom's, does not change with the block. */
export type SwathSource = 'boom' | 'nozzle_spacing' | 'row_spacing' | 'manual' | 'strip';

export interface EquipmentCalibration {
  id: number;
  equipment_id: number;
  method: CalibrationMethod;

  // nozzle method
  nozzle_flow_lpm?: Numeric;
  nozzle_count?: number | null;
  nozzle_spacing_cm?: Numeric;

  // tank_empty method
  test_volume_l?: Numeric;
  time_to_empty_sec?: number | null;

  // conditions the measurement is only valid at
  operating_pressure_bar?: Numeric;
  operating_rpm?: number | null;
  speed_kph: NumericValue;

  // derived
  total_flow_lpm: NumericValue;
  reference_swath_m: NumericValue;
  swath_source: SwathSource;
  /** Derived against reference_swath_m — for an airblast this is a reference
   *  figure only; the real rate is re-derived per block. */
  output_rate_lpha: NumericValue;

  calibrated_by?: number | null;
  calibrated_by_first_name?: string | null;
  calibrated_by_last_name?: string | null;
  calibrated_at: string;
  spray_application_id?: number | null;
  spray_reference_number?: string | null;
  notes?: string | null;
  created_at: string;

  // Only present on the "latest" endpoint
  age_days?: number | null;
  valid_days?: number | null;
  /** Past the org's advisory age limit. Prompts, never blocks. */
  is_stale?: boolean;
}

/** What the operator types. The server computes everything derived. */
export interface CalibrationInput {
  method: CalibrationMethod;
  nozzle_flow_lpm?: number | null;
  nozzle_count?: number | null;
  nozzle_spacing_cm?: number | null;
  test_volume_l?: number | null;
  time_to_empty_sec?: number | null;
  operating_pressure_bar?: number | null;
  operating_rpm?: number | null;
  speed_kph: number;
  reference_swath_m?: number | null;
  swath_source?: SwathSource;
  spray_application_id?: number | null;
  notes?: string;
}

/** The live preview shown as the operator types, before anything is stored. */
export interface CalibrationPreview {
  method: CalibrationMethod;
  total_flow_lpm: number;
  reference_swath_m: number;
  swath_source: SwathSource;
  speed_kph: number;
  output_rate_lpha: number | null;
  /** How far one tank goes at this rate. */
  hectares_per_tank: number | null;
  /** How long one tank lasts at this flow. */
  minutes_per_tank: number | null;
}

/** Per-target rate resolution, surfaced on the reconciliation. */
export interface OutputRatePerTarget {
  target_type: TargetType;
  target_id: number;
  target_name: string;
  area_hectares: number;
  row_spacing_m: number | null;
  output_rate_lpha: number | null;
  /** True when re-derived from this target's row spacing rather than borrowed. */
  derived: boolean;
}

// ============================================================================
// API RESPONSE WRAPPERS
// ============================================================================

export interface ApiListResponse<T> {
  success: boolean;
  data?: T[];
  total?: number;
  page?: number;
  totalPages?: number;
  error?: { message: string; code?: string };
}

export interface ApiSingleResponse<T> {
  success: boolean;
  data?: T;
  error?: { message: string; code?: string };
}

// Volume calculation helper type (used in UI)
export interface VolumeEstimate {
  effectiveAreaHa: number;
  estimatedChemicalL: number;
  tankMixes: number;
  tankSizeL?: number;
  earliestHarvestDate?: string | null;
}

export interface StockCheckResult {
  sufficient: boolean;
  required: number;
  available: number;
  shortfall: number;
  unit: string;
}

// ============================================================================
// Weather limits — the conditions a spray may run in
// ============================================================================

export type WeatherLimitMetric =
  | 'wind_speed' | 'wind_gust' | 'temperature' | 'humidity' | 'delta_t'
  | 'forecast_wind_speed' | 'forecast_wind_gust' | 'forecast_temperature'
  | 'forecast_delta_t' | 'forecast_rain';

export type WeatherLimitBound = 'max' | 'min';

/** One limit as sent to the server. `window_hours` is for forecast metrics only. */
export interface WeatherLimitInput {
  metric: WeatherLimitMetric;
  bound: WeatherLimitBound;
  threshold: number;
  window_hours?: number | null;
}

export interface WeatherLimit extends WeatherLimitInput {
  id: number;
}

/** How one limit stands, from the server's judgement. */
export interface WeatherLimitResult {
  limit_id: number | null;
  metric: WeatherLimitMetric;
  label: string;
  unit: string;
  kind: 'now' | 'forecast' | null;
  bound: WeatherLimitBound;
  threshold: number;
  window_hours: number | null;
  value: number | null;
  status: 'ok' | 'breach' | 'unknown';
  /** station | open_meteo | mixed (a reading now) or forecast */
  source: string | null;
  /** For a forecast value: the hour it happens. */
  forecast_at: string | null;
}

export interface WeatherLimitCheck {
  /** 'now' = conditions now; 'scheduled' = the forecast for the spray's scheduled start. */
  anchor: 'now' | 'scheduled';
  anchor_at: string;
  checked_at: string;
  breached: boolean;
  unknown: number;
  results: WeatherLimitResult[];
}

export interface WeatherLimitBreach {
  id: number;
  metric: WeatherLimitMetric;
  label: string;
  unit: string;
  bound: WeatherLimitBound;
  threshold: number;
  window_hours: number | null;
  observed_value: number | null;
  source: string | null;
  forecast_at: string | null;
  phase: 'planned' | 'in_progress' | 'paused' | 'start';
  opened_at: string;
  last_seen_at: string;
  cleared_at: string | null;
  notified_at: string | null;
  acknowledged_at: string | null;
  acknowledged_by_name: string | null;
}

export interface SprayWeatherLimits {
  limits: WeatherLimit[];
  check: WeatherLimitCheck | null;
  breaches: WeatherLimitBreach[];
}

export interface WeatherLimitPreset {
  id: number;
  name: string;
  description: string | null;
  limits: WeatherLimitInput[];
  created_at: string;
  updated_at: string;
}
